import { apiClient } from '@discovr/common';
import type {
  DiscoveryCampaignDetail,
  DiscoveryCancelBody,
  DiscoveryCancelled,
  DiscoveryChatPatch,
  DiscoveryChatStart,
  DiscoveryChatThread,
  DiscoveryChatThreadSummary,
  DiscoveryChatThreads,
  DiscoveryClarifyingQuestion,
  DiscoveryCostQuote,
  DiscoveryEstimateBody,
  DiscoveryGenerateBody,
  DiscoveryGeneratedPersonas,
  DiscoveryJobView,
  DiscoveryLiveFeed,
  DiscoveryMediaItem,
  DiscoveryMediaLinks,
  DiscoveryMediaLinksBody,
  DiscoveryPersona,
  DiscoveryPersonaChat,
  DiscoveryPersonaWrite,
  DiscoveryPersonaQuestionChecked,
  DiscoveryQuestionCheckBody,
  DiscoveryPersonaChatTurn,
  DiscoveryPersonaSearchBody,
  DiscoveryPersonaSearchPreviewBody,
  DiscoveryPersonaSearchPreviewed,
  DiscoveryPersonaStarters,
  DiscoveryPicks,
  DiscoveryPicksPatch,
  DiscoveryRejected,
  DiscoveryResults,
  DiscoverySearch,
  DiscoverySearchStarted,
  DiscoveryServiceCampaignPatch,
  DiscoveryTurnTrace,
  DiscoveryVettingCatalog,
} from '@discovr/types';
import { DISCOVERY_PATHS, PERSONA_REQUEST_TIMEOUT_MS, SEARCH_START_TIMEOUT_MS } from '../constants';

type RequestConfig = Parameters<typeof apiClient.get>[1];

const personaRequest: RequestConfig = { timeout: PERSONA_REQUEST_TIMEOUT_MS };
const searchStartRequest: RequestConfig = { timeout: SEARCH_START_TIMEOUT_MS };
const outlivesPageRequest: RequestConfig = { adapter: 'fetch', fetchOptions: { keepalive: true } };

const getData = async <TRes>(path: string, config?: RequestConfig) =>
  (await apiClient.get<TRes>(path, config)).data;
const postData = async <TRes, TBody>(path: string, body: TBody, config?: RequestConfig) =>
  (await apiClient.post<TRes>(path, body, config)).data;
const postEmpty = async <TRes>(path: string) => (await apiClient.post<TRes>(path)).data;
const patchData = async <TRes, TBody>(path: string, body: TBody, config?: RequestConfig) =>
  (await apiClient.patch<TRes>(path, body, config)).data;
const deleteData = async <TRes>(path: string) => (await apiClient.delete<TRes>(path)).data;

export const discoveryApi = {
  sync: (campaignId: string) =>
    postEmpty<DiscoveryCampaignDetail>(DISCOVERY_PATHS.sync(campaignId)),
  campaign: (campaignId: string) =>
    getData<DiscoveryCampaignDetail>(DISCOVERY_PATHS.campaign(campaignId)),
  patch: (campaignId: string, body: DiscoveryServiceCampaignPatch) =>
    patchData<DiscoveryCampaignDetail, DiscoveryServiceCampaignPatch>(
      DISCOVERY_PATHS.campaign(campaignId),
      body,
    ),
  personaStarters: (campaignId: string) =>
    getData<DiscoveryPersonaStarters>(DISCOVERY_PATHS.personaStarters(campaignId), personaRequest),
  vettingCatalog: (campaignId: string) =>
    getData<DiscoveryVettingCatalog>(DISCOVERY_PATHS.vettingCatalog(campaignId)),
  generatePersonas: (campaignId: string, count: number) =>
    postData<DiscoveryGeneratedPersonas, DiscoveryGenerateBody>(
      DISCOVERY_PATHS.generatePersonas(campaignId),
      { count },
      personaRequest,
    ),
  ensurePersonaSearch: (campaignId: string, personaIds: string[]) =>
    postData<DiscoveryCampaignDetail, DiscoveryPersonaSearchBody>(
      DISCOVERY_PATHS.personaSearch(campaignId),
      { personaIds },
      personaRequest,
    ),
  previewPersonaSearch: async (campaignId: string, persona: DiscoveryPersona) =>
    (
      await postData<DiscoveryPersonaSearchPreviewed, DiscoveryPersonaSearchPreviewBody>(
        DISCOVERY_PATHS.personaSearchPreview(campaignId),
        { persona },
        personaRequest,
      )
    ).search,
  chatPersona: (campaignId: string, body: DiscoveryPersonaChat) =>
    postData<DiscoveryPersonaChatTurn, DiscoveryPersonaChat>(
      DISCOVERY_PATHS.chatPersona(campaignId),
      body,
      personaRequest,
    ),
  writePersona: (campaignId: string, body: DiscoveryPersonaWrite) =>
    postData<DiscoveryPersonaChatTurn, DiscoveryPersonaWrite>(
      DISCOVERY_PATHS.writePersona(campaignId),
      body,
      personaRequest,
    ),
  checkPersonaQuestion: (campaignId: string, question: string) =>
    postData<DiscoveryPersonaQuestionChecked, DiscoveryQuestionCheckBody>(
      DISCOVERY_PATHS.checkPersonaQuestion(campaignId),
      { question },
    ),
  turnEvents: (campaignId: string, turnId: string, after: number) =>
    getData<DiscoveryTurnTrace>(DISCOVERY_PATHS.turnEvents(campaignId, turnId), {
      params: { after },
    }),
  chatThreads: (campaignId: string, personaId: string | null) =>
    getData<DiscoveryChatThreads>(DISCOVERY_PATHS.chats(campaignId), {
      params: personaId ? { persona: personaId } : undefined,
    }),
  chatThread: (campaignId: string, chatId: string) =>
    getData<DiscoveryChatThread>(DISCOVERY_PATHS.chat(campaignId, chatId)),
  patchChat: (campaignId: string, chatId: string, body: DiscoveryChatPatch) =>
    patchData<DiscoveryChatThreadSummary, DiscoveryChatPatch>(
      DISCOVERY_PATHS.chat(campaignId, chatId),
      body,
    ),
  startDraft: (campaignId: string, body: DiscoveryChatStart) =>
    postData<DiscoveryChatThreadSummary, DiscoveryChatStart>(
      DISCOVERY_PATHS.chats(campaignId),
      body,
      outlivesPageRequest,
    ),
  keepDraft: (campaignId: string, chatId: string, workingDraft: DiscoveryPersona) =>
    patchData<DiscoveryChatThreadSummary, DiscoveryChatPatch>(
      DISCOVERY_PATHS.chat(campaignId, chatId),
      { workingDraft },
      outlivesPageRequest,
    ),
  dropChat: (campaignId: string, chatId: string) =>
    deleteData<{ ok: true }>(DISCOVERY_PATHS.chat(campaignId, chatId)),
  estimate: (campaignId: string, body: DiscoveryEstimateBody) =>
    postData<DiscoveryCostQuote, DiscoveryEstimateBody>(DISCOVERY_PATHS.estimate(campaignId), body),
  startSearch: (campaignId: string, body: DiscoverySearch) =>
    postData<DiscoverySearchStarted | DiscoveryClarifyingQuestion, DiscoverySearch>(
      DISCOVERY_PATHS.searches(campaignId),
      body,
      searchStartRequest,
    ),
  search: (campaignId: string, jobId: string) =>
    getData<DiscoveryJobView>(DISCOVERY_PATHS.search(campaignId, jobId)),
  results: (campaignId: string, jobId: string) =>
    getData<DiscoveryResults>(DISCOVERY_PATHS.results(campaignId, jobId)),
  live: (campaignId: string, jobId: string, after: number) =>
    getData<DiscoveryLiveFeed>(DISCOVERY_PATHS.live(campaignId, jobId), { params: { after } }),
  rejected: (campaignId: string, jobId: string) =>
    getData<DiscoveryRejected>(DISCOVERY_PATHS.rejected(campaignId, jobId)),
  cancel: (campaignId: string, jobId: string, keepPartial: boolean) =>
    postData<DiscoveryCancelled, DiscoveryCancelBody>(DISCOVERY_PATHS.cancel(campaignId, jobId), {
      keepPartial,
    }),
  picks: (campaignId: string) => getData<DiscoveryPicks>(DISCOVERY_PATHS.picks(campaignId)),
  patchPicks: (campaignId: string, body: DiscoveryPicksPatch) =>
    patchData<DiscoveryPicks, DiscoveryPicksPatch>(DISCOVERY_PATHS.picks(campaignId), body),
  mediaLinks: (campaignId: string, items: DiscoveryMediaItem[]) =>
    postData<DiscoveryMediaLinks, DiscoveryMediaLinksBody>(DISCOVERY_PATHS.mediaLinks(campaignId), {
      items,
    }),
};

/** Absolute URL of a signed media link, for `<img>` and `<video>` which cannot send the bearer token. */
export const mediaUrl = (token: string) =>
  `${apiClient.defaults.baseURL ?? ''}${DISCOVERY_PATHS.media(token)}`;
